import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, toast } from "@dl/web-core-js/ui";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { isoDate } from "../../api/reporting";
import {
  isIssuable,
  ProductionOrderClient,
  type ProductionOrderDTO,
  type ProductionOrderMaterialDTO,
  type ProductionPostResult,
} from "../../api/production";

/**
 * Materials taken from stock for a run.
 *
 * <p>Each line opens empty: an issue is usually a few lines picked for the
 * next step, not the whole tree. "Fill what is short" sets every line to what
 * the run still needs. Issuing more than needed is allowed — a cracked sheet
 * happens — and to put material back, reverse the issue in the ledger.</p>
 */
export function IssueDialog({
  order,
  materials,
  onClose,
  onPosted,
}: {
  order: ProductionOrderDTO;
  materials: ProductionOrderMaterialDTO[];
  onClose: () => void;
  onPosted: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const lines = materials.filter(isIssuable);
  const [quantities, setQuantities] = useState<Record<number, string>>({});
  const [date, setDate] = useState(() => isoDate(new Date()));
  const [result, setResult] = useState<ProductionPostResult | null>(null);

  const chosen = lines
    .map((line) => ({ line, quantity: Number(quantities[line.id]) }))
    .filter(({ quantity }) => Number.isFinite(quantity) && quantity > 0);
  // Anything typed that is not above zero (a negative, a lone "-") would be dropped silently; it blocks the post instead.
  const invalid = (line: ProductionOrderMaterialDTO) => {
    const typed = quantities[line.id]?.trim() ?? "";
    return typed !== "" && !(Number(typed) > 0) && Number(typed) !== 0;
  };
  const anyInvalid = lines.some(invalid);

  const post = useMutation({
    mutationFn: () =>
      orders.issue(order.id, {
        document_date: date || null,
        note: null,
        lines: chosen.map(({ line, quantity }) => ({ production_order_material_id: line.id, quantity })),
      }),
    onSuccess: async (posted) => {
      setResult(posted);
      await onPosted();
      toast.success(t("mo.issued", "Issue #{{id}} posted", { id: posted.stock_document_id }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={t("mo.issue.title", "Issue to order {{n}}", { n: order.number })}
      actions={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("button.close", "Close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button variant="primary" disabled={chosen.length === 0 || anyInvalid} loading={post.isPending} onClick={() => post.mutate()}>
              {chosen.length === 1 ? t("mo.issue.post-one", "Issue 1 line") : t("mo.issue.post", "Issue {{n}} lines", { n: chosen.length })}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <PostResultTable result={result} />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t("form.field.date", "Date")} htmlFor="mo-issue-date">
              <Input id="mo-issue-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </Field>
            <Button
              variant="ghost"
              size="sm"
              disabled={lines.length === 0}
              onClick={() =>
                setQuantities(
                  Object.fromEntries(
                    lines.map((line) => [line.id, Number(line.quantity_remaining ?? 0) > 0 ? String(line.quantity_remaining) : ""]),
                  ),
                )
              }
            >
              {t("mo.issue.fill", "Fill what is short")}
            </Button>
          </div>
          {lines.length === 0 ? (
            <p className="text-[13px] text-ink-muted">{t("mo.issue.none", "Nothing on this order is issued by hand: its materials are made here or not tracked.")}</p>
          ) : (
          <div className="max-h-[50vh] overflow-auto rounded-md border border-line">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-faint">
                <tr>
                  <th className="px-3 py-2">{t("column.part.number", "Number")}</th>
                  <th className="px-3 py-2 text-right">{t("column.mo.required", "Required")}</th>
                  <th className="px-3 py-2 text-right">{t("mo.issue.short", "Short")}</th>
                  <th className="px-3 py-2 text-right">{t("mo.issue.now", "Issue")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-t border-line">
                    <td className="px-3 py-1.5">
                      <span className="font-medium">{line.part_number}</span>
                      <span className="ml-2 text-[11px] text-ink-faint">{line.part_name}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatNumber(line.quantity_required)} <span className="text-[11px] text-ink-faint">{line.stock_unit_code}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(line.quantity_remaining)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={quantities[line.id] ?? ""}
                        invalid={invalid(line)}
                        aria-label={t("mo.issue.now", "Issue")}
                        onChange={(event) => setQuantities((current) => ({ ...current, [line.id]: event.target.value }))}
                        className="ml-auto h-8 w-24 text-right"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          {anyInvalid && <p className="text-[12px] text-risk">{t("mo.issue.invalid", "A quantity to issue must be above zero.")}</p>}
        </div>
      )}
    </Dialog>
  );
}

/**
 * Finished goods off the line.
 *
 * <p>Opens at what is left to make. Every tracked material is used up with
 * the output, topped up to its share of everything produced so far, so a hand
 * issue is never taken twice.</p>
 */
export function OutputDialog({
  order,
  onClose,
  onPosted,
}: {
  order: ProductionOrderDTO;
  onClose: () => void;
  onPosted: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const left = Math.max(0, Number(order.quantity) - Number(order.quantity_produced ?? 0));
  const [quantity, setQuantity] = useState(left > 0 ? String(left) : "");
  const [date, setDate] = useState(() => isoDate(new Date()));
  const [result, setResult] = useState<ProductionPostResult | null>(null);
  const amount = Number(quantity);

  const post = useMutation({
    mutationFn: () => orders.output(order.id, { quantity: amount, document_date: date || null, note: null }),
    onSuccess: async (posted) => {
      setResult(posted);
      await onPosted();
      toast.success(t("mo.output.posted", "Output #{{id}} posted", { id: posted.stock_document_id }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("mo.output.title", "Post output for order {{n}}", { n: order.number })}
      actions={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("button.close", "Close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button variant="primary" disabled={!(amount > 0)} loading={post.isPending} onClick={() => post.mutate()}>
              {t("mo.output", "Post output")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <PostResultTable result={result} />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t("mo.output.quantity", "Made")}
              htmlFor="mo-output-quantity"
              hint={t("mo.output.left", "{{n}} {{unit}} left to make", { n: formatNumber(left), unit: order.stock_unit_code ?? "" })}
              required
            >
              <Input
                id="mo-output-quantity"
                type="number"
                step="any"
                min="0"
                value={quantity}
                invalid={quantity.trim() !== "" && !(amount > 0)}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </Field>
            <Field label={t("form.field.date", "Date")} htmlFor="mo-output-date">
              <Input id="mo-output-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </Field>
          </div>
          <p className="text-[12px] text-ink-muted">
            {t("mo.output.hint-tracked", "Every tracked material is used up to its share of what has been made. What was already issued counts.")}
          </p>
        </div>
      )}
    </Dialog>
  );
}

function PostResultTable({ result }: { result: ProductionPostResult }) {
  const { t } = useTranslation();
  return (
    <div className="max-h-[50vh] overflow-auto">
      <table className="w-full text-[13px] tabular-nums">
        <thead className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
          <tr>
            <th className="py-1">{t("column.part.number", "Number")}</th>
            <th className="py-1">{t("column.stock.kind", "Kind")}</th>
            <th className="py-1 text-right">{t("column.bom.quantity", "Qty")}</th>
          </tr>
        </thead>
        <tbody>
          {result.lines.map((line) => (
            <tr key={line.stock_movement_id} className="border-t border-line">
              <td className="py-1">
                {line.part_number}
                {line.backflushed && (
                  <Badge variant="info" size="sm" className="ml-2">
                    {t("mo.material.backflush", "used up")}
                  </Badge>
                )}
              </td>
              <td className="py-1">{line.kind === "ISSUE" ? t("mo.kind.issue", "Issued") : t("mo.kind.output", "Made")}</td>
              <td className="py-1 text-right">{formatNumber(line.quantity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
